import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Earth, Handshake, ShieldCheck } from 'lucide-react';

const benefitItems = [
  {
    title: 'Climate Action',
    icon: <Earth size={42} className="text-[#703aa4]" />,
    description: 'Involvement in positive climate action which lowers your carbon footprint.',
  },
  {
    title: 'Community Support',
    icon: <Handshake size={42} className="text-[#703aa4]" />,
    description: 'Empowering local communities to take control of their energy production.',
  },
  {
    title: 'Zero Maintenance',
    icon: <ShieldCheck size={42} className="text-[#703aa4]" />,
    description: 'Innovative solutions to reduce energy consumption and lower costs.',
  },
];

const useReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1 });

    if (currentRef) observer.observe(currentRef);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

const MainPage = () => {
  const [heroRef, heroVisible] = useReveal();
  const [coopRef, coopVisible] = useReveal();
  const [benefitsRef, benefitsVisible] = useReveal();
  const [coopInfoRef, coopInfoVisible] = useReveal();
  const [dividerRef, dividerVisible] = useReveal();
  const [orangeDividerRef, orangeDividerVisible] = useReveal();

  return (
    <div className="flex-grow -mx-4 sm:-mx-6 lg:-mx-8">
      <section
        ref={heroRef}
        className={`relative min-h-[340px] sm:min-h-[390px] lg:min-h-[430px] flex items-center overflow-hidden transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        style={{
          background: 'linear-gradient(145deg, #0a1628 0%, #162d50 25%, #1e3a5f 50%, #1a4a7a 75%, #0d2840 100%)',
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              radial-gradient(
                circle at 82% 8%,
                rgba(255, 255, 255, 0.08) 0,
                rgba(255, 255, 255, 0.03) 18%,
                transparent 42%
              ),
              linear-gradient(
                135deg,
                rgba(255, 255, 255, 0.04) 0,
                transparent 35%,
                rgba(0, 0, 0, 0.12) 100%
              ),
              repeating-linear-gradient(
                145deg,
                transparent,
                transparent 92px,
                rgba(255, 255, 255, 0.055) 92px,
                rgba(255, 255, 255, 0.055) 94px
              ),
              repeating-linear-gradient(
                55deg,
                transparent,
                transparent 64px,
                rgba(255, 255, 255, 0.04) 64px,
                rgba(255, 255, 255, 0.04) 66px
              )
            `,
          }}
        />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 lg:py-16">
          <div className={`max-w-3xl transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <img
              src="/media/logo-icon.svg"
              alt="Energy Democracy logo"
              className="w-12 h-12 mb-6"
            />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-normal text-white leading-tight">
              Renewable energy in your hands
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/95 leading-relaxed max-w-2xl">
              Energy Democracy helps communities reduce carbon emissions by building, operating and maintaining renewable energy parks owned through local co-operatives.
            </p>
            <a
              href="https://energydemocracy.us14.list-manage.com/subscribe?u=531274272281a368367bd1700&id=ab566fa072"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 bg-[#ff8400] hover:bg-[#e67600] text-white text-lg font-semibold shadow-md py-3 px-6 rounded-full transition duration-300"
            >
              Subscribe to our newsletter
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <section
          ref={coopRef}
          className={`my-12 sm:my-16 transition-all duration-700 ${coopVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-10 items-stretch">
            <div className={`flex flex-col justify-center transition-all duration-700 delay-100 ${coopVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <p className="text-sm font-semibold uppercase tracking-widest text-[#ff8400] mb-3">Community energy</p>
              <h2 className="text-3xl sm:text-4xl text-[#703aa4] leading-tight">
                What is a renewable energy co-operative?
              </h2>
            </div>
            <div
              className={`hidden lg:block w-1 bg-[#ff8400] transition-all duration-700 delay-150 ${coopVisible ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-75'}`}
              aria-hidden="true"
            />
            <div className={`transition-all duration-700 delay-200 ${coopVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <p className="text-xl text-gray-600 mb-4 leading-relaxed">
                As the movement toward cleaner energy gains momentum, a growing number of community-scale renewable energy facilities are being constructed across Australia.
              </p>
              <p className="text-xl text-gray-600 leading-relaxed">
                Community-scale solar parks capture the economies of scale of larger solar farms while keeping the benefits closer to the communities they serve.
              </p>
            </div>
          </div>
        </section>

        <div
          ref={dividerRef}
          className={`flex justify-center my-12 transition-all duration-500 ${dividerVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}
        >
          <img src="/media/logo-icon.svg" alt="" className="w-10 h-10" aria-hidden="true" />
        </div>

        <section ref={benefitsRef} className="my-12 sm:my-16">
          <h2
            className={`text-3xl text-gray-800 text-center mb-8 transition-all duration-700 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            Community-owned solar benefits
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3">
            {benefitItems.map((item, index) => (
              <div
                key={item.title}
                className={`py-6 md:px-8 md:first:pl-0 md:last:pr-0 border-b md:border-b-0 md:border-r last:border-b-0 md:last:border-r-0 border-gray-300 transition-all duration-700 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: benefitsVisible ? `${150 + index * 150}ms` : '0ms' }}
              >
                <div className="mb-5">{item.icon}</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-800">{item.title}</h3>
                <p className="text-xl text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        <div
          ref={orangeDividerRef}
          className={`my-12 border-t-4 border-[#ff8400] transition-all duration-500 origin-center ${orangeDividerVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-75'}`}
          aria-hidden="true"
        />

        <div 
          ref={coopInfoRef}
          className={`mb-0 transition-all duration-700 relative overflow-hidden ${coopInfoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-center">
            <img
              src="/media/logo-icon.svg"
              alt="Energy Democracy Logo"
              className={`w-14 h-14 md:w-20 md:h-20 transition-all duration-500 delay-300 ${coopInfoVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}
            />
            <div>
              <h2 className={`text-3xl mb-4 text-[#703aa4] transition-all duration-500 delay-200 ${coopInfoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>Energy Democracy Co-operative</h2>
              <p className={`text-xl text-gray-600 mb-4 leading-relaxed transition-all duration-500 delay-400 ${coopInfoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                Our members have invested in solar and batteries and benefit from the financial returns.
              </p>
              <p className={`text-xl text-gray-600 mb-0 leading-relaxed transition-all duration-500 delay-500 ${coopInfoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                Owning shares in our co-operative provides investors with the competitive financial and environmental benefits of renewable energy. The park includes battery storage, which we use to generate extra income for the co-operative that can be distributed to the members.
              </p>
            </div>
          </div>
        </div>

        {/* Disclaimer Section */}
        <div className="mb-8 mt-8 pt-8 border-t border-gray-200">
          <hr className="border-t border-gray-600 w-full mb-4" />
            <p className="text-sm text-gray-600">
              * Please read the <a href="/media/Energy%20Democracy%20Disclosure%20Statement-2e79b172bc661b833ded6066d87fd44f.pdf" target="_blank" rel="noreferrer" className="text-[#703aa4] underline">Disclosure Statement</a>. No application for relief under section 741 of the Corporations Act 2001 (Cth) will be sought by the Energy Democracy Central West NSW Co-operative Limited ('the Co-operative') in relation to any statement made in this document as the Chapter 6D disclosure provisions do not apply to members' shares in the Co-operative.
            </p>
        </div>
      </div>            
    </div>
  );
};

export default MainPage;
